import Collapse from "@material-ui/core/Collapse";
import { createStyles, Theme, withStyles, WithStyles } from "@material-ui/core/styles";
import { darken } from "@material-ui/core/styles/colorManipulator";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import Tooltip from "@material-ui/core/Tooltip";
import classNames from "classnames";
import React from "react";
import { TableBodyColumnSchema } from "../models";
import { TableBodyRowTable } from "./TableBodyRowTable";
import { TableCellContent } from "./TableCellContent";

const styles = (theme: Theme) =>
  createStyles({
    row: {
      backgroundColor: theme.palette.background.paper,
    },
    rowCell: {
      color: theme.palette.text.primary,
      fontSize: theme.typography.body2.fontSize,
    },
    expandableRow: {
      cursor: "pointer",
    },
    expandableRowCell: {
      paddingTop: 0,
      paddingBottom: 0,
      backgroundColor: darken(theme.palette.background.paper, theme.palette.type === "light" ? 0.025 : 0.05),
      boxShadow: `inset 0px ${theme.spacing(1)}px ${theme.spacing(1)}px -${theme.spacing(
        1,
      )}px rgba(0,0,0,0.12), inset 0px -${theme.spacing(1)}px ${theme.spacing(1)}px -${theme.spacing(
        1,
      )}px rgba(0,0,0,0.12)`,
    },
  });

interface TableBodyRowProps extends WithStyles<typeof styles> {
  index: number;
  data: any;
  columnSchemas: TableBodyColumnSchema[];
  expandableProps?: {
    data?: any;
    columnSchemas: TableBodyColumnSchema[];
    isExpanded: boolean;
    onExpandedToggle: () => void;
  };
}

export const TableBodyRowComponent = (props: TableBodyRowProps) => {
  const { columnSchemas, index, data, expandableProps, classes } = props;

  let expandableRowProps:
    | {
        isExpanded: boolean;
        expandableRowCollapseComponent: () => JSX.Element;
        onExpandableRowClick: () => void;
      }
    | undefined;

  if (expandableProps && expandableProps.data != null) {
    const { data, columnSchemas: expandableRowColumnSchemas, isExpanded, onExpandedToggle } = expandableProps;

    const expandableRowCollapseComponent = () => (
      <TableRow tabIndex={-1}>
        <TableCell colSpan={columnSchemas.length} className={classes.expandableRowCell}>
          <TableBodyRowTable parentRowIndex={index} columnSchemas={expandableRowColumnSchemas} data={data} />
        </TableCell>
      </TableRow>
    );

    expandableRowProps = {
      isExpanded,
      expandableRowCollapseComponent,
      onExpandableRowClick: onExpandedToggle,
    };
  }

  let row = (
    <TableRow
      hover={true}
      tabIndex={-1}
      className={classNames(classes.row, expandableRowProps && classes.expandableRow)}
      onClick={expandableRowProps?.onExpandableRowClick}
    >
      {columnSchemas.map((columnSchema, cellIndex) => (
        <TableCell
          key={`bc-${cellIndex}`}
          align={columnSchema.align}
          padding={columnSchema.padding}
          size={columnSchema.size}
          className={classes.rowCell}
          style={{ width: columnSchema.width, display: columnSchema.hidden ? "none" : undefined }}
        >
          <TableCellContent
            columnId={columnSchema.id}
            rowData={data}
            format={columnSchema.format}
            emptyValue={columnSchema.emptyValue}
            cellRenderer={columnSchema.cellRenderer}
          />
        </TableCell>
      ))}
    </TableRow>
  );

  if (expandableRowProps) {
    row = (
      <>
        <Tooltip title={`${expandableRowProps.isExpanded ? "Hide" : "Show"} Details`} enterDelay={300}>
          {row}
        </Tooltip>
        <Collapse
          component={expandableRowProps.expandableRowCollapseComponent}
          in={expandableRowProps.isExpanded}
          timeout={0}
          unmountOnExit={true}
        />
      </>
    );
  }

  return row;
};

export const TableBodyRow = React.memo(withStyles(styles)(TableBodyRowComponent));
